/**
 * Legacy redirects owned by the React page router rather than Express.
 * The SSR regionless-path guard and page router both use this registry.
 */
export const LEGACY_REGIONLESS_REDIRECTS = [
  {
    from: '/velocity-frequent-flyer',
    statusCode: 301,
    to: '/',
  },
] as const

export type LegacyRegionlessRedirect =
  typeof LEGACY_REGIONLESS_REDIRECTS[number]

/** Exact regionless routes rendered by routers outside PageRoutes. */
export const REGIONLESS_DIRECT_ROUTE_PATHS = [
  '/oidc-callback',
] as const

/**
 * Dynamic first-level page routes rendered by PageRoutes. They must bypass the
 * SSR regionless-path guard so React can render or redirect them.
 */
export const REGIONLESS_DYNAMIC_ROUTE_SUFFIXES = {
  contentHub: 'p1',
  destination: 'd1',
  holiday: 'h1',
} as const

/**
 * First-level page-route segments that intentionally receive the request
 * region as a prefix (for example, `/tours` becomes `/:regionCode/tours`).
 * Keep this list aligned with PageRoutes; its route-object test enforces that.
 */
export const REGIONLESS_FALLBACK_ROUTE_ROOTS = [
  'NHCollectionDubaiThePalm',
  'abercrombie-and-kent-tours.p1',
  'account',
  'account-details',
  'about-us',
  'affiliate-program',
  'amex',
  'ancillary-change-reservation',
  'ancillary-payment',
  'app',
  'bb',
  'best-price-guarantee',
  'best-price-guarantee-terms',
  'booking',
  'booking-policies',
  'booking-authority',
  'booking-form',
  'buy-now-book-later',
  'campaign',
  'car-hire',
  'careers',
  'cars',
  'cart',
  'chadstone.p1',
  'checkout',
  'checkout-gateway',
  'checkout-restore-cart',
  'citi',
  'confirmation',
  'corporate-events',
  'cruise-line',
  'cruises',
  'destination',
  'disneyland-offers',
  'disneyland-offers-test',
  'doublesix',
  'escape-with-confidence',
  'escapes-finder',
  'experience',
  'experiences',
  'flight',
  'flights',
  'flights-search',
  'flights-search-results',
  'flights-search-results-multi-city',
  'flights-policies',
  'frequent-flyer-programs',
  'frequently-asked-questions',
  'friends',
  'gift-card',
  'gift-cards',
  'giftcard',
  'giftcards',
  'giftcards-terms',
  'google-hotel-ads',
  'holiday-packages',
  'holidays',
  'hotels',
  'hoolah',
  'hunter-valley-competition',
  'indigo',
  'insurance',
  'live',
  'lux-plus',
  'lux-plus-terms-and-conditions',
  'luxbundles',
  'luxpay',
  'luxplus',
  'metasearch',
  'my-escapes',
  'my-subscriptions',
  'nyc',
  'offer',
  'order',
  'orders',
  'page',
  'pages',
  'partner',
  'passport-card-insurance',
  'pay',
  'property',
  'privacy-policy',
  'promotions',
  'purchase-credits',
  'redeem',
  'refer',
  'refund-policy',
  'rentals',
  'request-video-chat',
  'reset-password',
  'resorts',
  'resubscribe',
  'review',
  'search',
  'search-fullscreen',
  'sharethis',
  'shop',
  'shop-faqs',
  'shop-terms-and-conditions',
  'signature-series.p1',
  'skycheck',
  'splashpage',
  'society',
  'société',
  'societe',
  'societe-faqs',
  'societe-terms-and-conditions',
  'spin-the-wheel-competition',
  'spotify',
  'support',
  'tailor-made-tours',
  'terms',
  'terms-and-conditions',
  'top-picks-for-me',
  'tour',
  'tour-with-confidence',
  'tours',
  'transfers',
  'travel-protection',
  'traveltester',
  'trip-planner',
  'user-cart',
  'villas',
  'weddings',
  'wishlists',
  'afterpay',
  'atome',
  'klarna',
  'shopbackpaylater',
  'ultra-lux',
] as const

const legacyRegionlessRedirectPaths = new Set<string>(
  LEGACY_REGIONLESS_REDIRECTS.map(redirect => redirect.from.toLowerCase()),
)

const regionlessDirectRoutePaths = new Set<string>(
  REGIONLESS_DIRECT_ROUTE_PATHS.map(pathname => pathname.toLowerCase()),
)

const regionlessFallbackRoutePaths = new Set<string>(
  REGIONLESS_FALLBACK_ROUTE_ROOTS.map(routeRoot => `/${routeRoot.toLowerCase()}`),
)

const regionlessDynamicRoutePattern = new RegExp(
  `^/[^/]+\\.(${Object.values(REGIONLESS_DYNAMIC_ROUTE_SUFFIXES).join('|')})$`,
)

function normaliseRegionlessPathname(pathname: string): string {
  try {
    // Match React Router's decoding without turning encoded slashes into segments.
    return pathname.split('/').map(
      segment => decodeURIComponent(segment).replace(/\//g, '%2F'),
    ).join('/').toLowerCase()
  } catch {
    return pathname.toLowerCase()
  }
}

export function isLegacyRegionlessRedirect(pathname: string): boolean {
  return legacyRegionlessRedirectPaths.has(normaliseRegionlessPathname(pathname))
}

export function isRegionlessDirectRoute(pathname: string): boolean {
  return regionlessDirectRoutePaths.has(normaliseRegionlessPathname(pathname))
}

export function isRegionlessFallbackRoute(pathname: string): boolean {
  return regionlessFallbackRoutePaths.has(normaliseRegionlessPathname(pathname))
}

export function isRegionlessDynamicRoute(pathname: string): boolean {
  return regionlessDynamicRoutePattern.test(normaliseRegionlessPathname(pathname))
}
